import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { Plus, FileText, Clock, CheckCircle2 } from "lucide-react";

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/projects")
      .then(({ data }) => { if (!cancelled) setProjects(data.projects || []); })
      .catch((err) => { if (!cancelled) console.warn("Failed to load projects:", err?.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const statusBadge = (s) => {
    const map = {
      interview: { label: "In Progress", color: "var(--bw-warning)" },
      summary: { label: "Summary Ready", color: "var(--bw-brand)" },
      paid: { label: "Premium Unlocked", color: "var(--bw-success)" },
      concierge: { label: "Concierge Active", color: "var(--bw-accent)" },
    };
    const cfg = map[s] || { label: s, color: "var(--bw-text-secondary)" };
    return (
      <span className="text-xs uppercase font-bold tracking-widest px-2 py-1 border" style={{ color: cfg.color, borderColor: cfg.color }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Your Projects — BuildWise AI Dashboard"
        description="Manage your active San Diego building projects, feasibility reports, and concierge submissions in one place."
        path="/dashboard"
      />
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-overline mb-3">Your Dashboard</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight" style={{ color: "var(--bw-text-primary)" }}>
              Projects
            </h1>
          </div>
          <Link to="/new" className="btn-primary" data-testid="new-project-btn">
            <Plus className="w-4 h-4" strokeWidth={2} /> New Project
          </Link>
        </div>

        <DashboardBody loading={loading} projects={projects} statusBadge={statusBadge} />
      </div>
    </div>
  );
}

function DashboardBody({ loading, projects, statusBadge }) {
  if (loading) return <div className="label-overline">Loading…</div>;

  if (projects.length === 0) {
    return (
      <div className="bw-card p-16 text-center">
        <FileText className="w-10 h-10 mx-auto mb-4" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
        <h3 className="font-display font-bold text-2xl mb-2" style={{ color: "var(--bw-text-primary)" }}>No projects yet</h3>
        <p className="text-sm mb-6" style={{ color: "var(--bw-text-secondary)" }}>Start your first BuildWise analysis in under a minute.</p>
        <Link to="/new" className="btn-primary inline-flex" data-testid="empty-new-project">
          <Plus className="w-4 h-4" /> Start First Project
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {projects.map((p) => (
        <Link
          key={p.id}
          to={`/project/${p.id}`}
          className="bw-card p-6 hover:shadow-sm transition-all"
          data-testid={`project-card-${p.id}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg" style={{ color: "var(--bw-text-primary)" }}>
              {p.project_type}
            </div>
            {statusBadge(p.status)}
          </div>
          <div className="text-sm mb-2" style={{ color: "var(--bw-text-secondary)" }}>{p.address}</div>
          <div className="text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
            {p.jurisdiction || "Jurisdiction pending"}
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
            {p.status === "paid" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
            {new Date(p.created_at).toLocaleDateString()}
          </div>
        </Link>
      ))}
    </div>
  );
}
